import { BM, } from './backend';
const CLASS_NAME_REG_EXP = /[^\s.,]+/g;
const matchClassName = (str) => {
    const result = [];
    let m;
    CLASS_NAME_REG_EXP.lastIndex = 0;
    // eslint-disable-next-line no-cond-assign
    while ((m = CLASS_NAME_REG_EXP.exec(str)) !== null) {
        result.push(String(m[0]));
    }
    return result;
};
/**
 * Style scope manager
 */
export class StyleScopeManager {
    constructor() {
        /** @internal */
        this._$names = [''];
    }
    static globalScope() {
        return 0;
    }
    register(name) {
        const ret = this._$names.length;
        this._$names.push(name);
        return ret;
    }
    queryName(id) {
        return this._$names[id];
    }
}
/**
 * Class manager for non-virtual `Element`
 */
export class ClassList {
    constructor(element, externalNames, owner, styleScope, extraStyleScope, styleScopeManager) {
        /** @internal */
        this._$externalNames = undefined;
        /** @internal */
        this._$externalRawAlias = null;
        /** @internal */
        this._$dirtyExternalNames = null;
        /** @internal */
        this._$rawNames = [];
        /** @internal */
        this._$backendNames = [];
        /** @internal */
        this._$backendNameScopes = [];
        /** @internal */
        this._$backendNamesCount = [];
        /** @internal */
        this._$hasAliasNames = false;
        this._$element = element;
        this._$owner = owner;
        this._$defaultScope = styleScope;
        this._$extraScope = extraStyleScope;
        // root owner got globalScope as it's styleScope, avoid the root owner
        this._$rootScope = (owner === null || owner === void 0 ? void 0 : owner._$owner) ? owner._$rootScope : styleScope;
        this._$prefixManager = styleScopeManager;
        if (externalNames) {
            this._$externalNames = externalNames;
            this._$externalRawAlias = [];
        }
    }
    /** @internal */
    _$resolvePrefixes(name, cb) {
        const owner = this._$owner;
        const externalNames = owner === null || owner === void 0 ? void 0 : owner._$externalNames;
        const externalIndex = externalNames ? externalNames.indexOf(name) : -1;
        if (owner && externalIndex !== -1) {
            this._$hasAliasNames = true;
            const rawAlias = owner._$externalRawAlias[externalIndex];
            if (rawAlias) {
                for (let i = 0; i < rawAlias.length; i += 1) {
                    owner._$resolvePrefixes(rawAlias[i], cb);
                }
            }
        }
        else if (name[0] === '~') {
            cb(this._$rootScope, name.slice(1));
        }
        else if (name[0] === '^') {
            let n = name.slice(1);
            let owner = this._$owner;
            while (n[0] === '^') {
                n = n.slice(1);
                owner = owner === null || owner === void 0 ? void 0 : owner._$owner;
            }
            // root owner got globalScope as it's styleScope, avoid the root owner
            const scopeId = (owner === null || owner === void 0 ? void 0 : owner._$owner) ? owner._$defaultScope : this._$rootScope;
            cb(scopeId, n);
        }
        else {
            if (this._$extraScope !== undefined) {
                cb(this._$extraScope, name);
            }
            cb(this._$defaultScope, name);
        }
    }
    /** @internal */
    _$hasAlias(name) {
        return !!this._$externalNames && this._$externalNames.includes(name);
    }
    /** @internal */
    _$setAlias(name, target) {
        if (!this._$externalNames)
            return;
        let slices;
        if (target === undefined || target === null) {
            slices = [];
        }
        else if (Array.isArray(target)) {
            slices = Array(target.length);
            for (let i = 0, l = target.length; i < l; i += 1) {
                slices[i] = String(target[i]);
            }
        }
        else {
            slices = matchClassName(String(target));
        }
        const externalIndex = this._$externalNames.indexOf(name);
        if (externalIndex === -1)
            return;
        this._$dirtyExternalNames = this._$dirtyExternalNames || [];
        this._$dirtyExternalNames.push(name);
        this._$externalRawAlias[externalIndex] = slices;
    }
    /** @internal */
    _$getAlias() {
        var _a;
        const result = Object.create(null);
        (_a = this._$externalNames) === null || _a === void 0 ? void 0 : _a.forEach((externalName, index) => {
            result[externalName] = this._$externalRawAlias[index];
        });
        return result;
    }
    /** @internal */
    _$spreadExternalClassUpdate() {
        const owner = this._$owner;
        if (!(owner === null || owner === void 0 ? void 0 : owner._$dirtyExternalNames) || !this._$hasAliasNames)
            return false;
        const dirtyExternalNames = owner._$dirtyExternalNames;
        const externalNames = this._$externalNames;
        if (externalNames) {
            for (let externalIndex = 0; externalIndex < externalNames.length; externalIndex += 1) {
                const externalRawAlias = this._$externalRawAlias[externalIndex] || [];
                for (let i = 0; i < dirtyExternalNames.length; i += 1) {
                    if (externalRawAlias.includes(dirtyExternalNames[i])) {
                        this._$dirtyExternalNames = this._$dirtyExternalNames || [];
                        this._$dirtyExternalNames.push(externalNames[externalIndex]);
                    }
                }
            }
        }
        const shouldUpdate = this._$rawNames.some((names) => names.some((name) => dirtyExternalNames.includes(name)));
        if (shouldUpdate) {
            this._$updateResolvedNames();
        }
        return !!this._$dirtyExternalNames || shouldUpdate;
    }
    /** @internal */
    _$shouldUpdateExternalClass() {
        var _a;
        if (!this._$dirtyExternalNames)
            return false;
        if (BM.SHADOW || (BM.DYNAMIC && this._$element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            const dirtyExternalNames = this._$dirtyExternalNames;
            for (let i = 0; i < dirtyExternalNames.length; i += 1) {
                const name = dirtyExternalNames[i];
                const externalIndex = this._$externalNames.indexOf(name);
                const targets = this._$externalRawAlias[externalIndex] || [];
                (_a = this._$element._$backendElement) === null || _a === void 0 ? void 0 : _a.setClassAlias(name, targets);
            }
            this._$dirtyExternalNames = null;
            return false;
        }
        return true;
    }
    /** @internal */
    _$markExternalClassUpdated() {
        this._$dirtyExternalNames = null;
    }
    /** @internal */
    _$fasterAddUpdateResolvedNames() {
        const backendElement = this._$element._$backendElement;
        if (!backendElement)
            return false;
        const rawNames = this._$rawNames;
        this._$hasAliasNames = false;
        for (let i = 0, l = rawNames.length; i < l; i += 1) {
            const names = rawNames[i];
            if (!names)
                continue;
            for (let j = 0, ll = names.length; j < ll; j += 1) {
                const rawName = names[j];
                if (BM.SHADOW || (BM.DYNAMIC && this._$element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                    this._$addClass(rawName, undefined, backendElement);
                }
                else {
                    this._$resolvePrefixes(rawName, (scopeId, className) => {
                        this._$addClass(className, scopeId, backendElement);
                    });
                }
            }
        }
        return true;
    }
    /** @internal */
    _$updateResolvedNames() {
        const backendElement = this._$element._$backendElement;
        if (!backendElement)
            return false;
        const rawNames = this._$rawNames;
        const oldBackendNames = this._$backendNames;
        const oldBackendNameScopes = this._$backendNameScopes;
        const oldBackendNamesCount = this._$backendNamesCount;
        const newBackendNames = [];
        const newBackendNameScopes = [];
        const newBackendNamesCount = [];
        this._$hasAliasNames = false;
        if (BM.SHADOW || (BM.DYNAMIC && this._$element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
            for (let i = 0, l = rawNames.length; i < l; i += 1) {
                const names = rawNames[i];
                if (!names)
                    continue;
                for (let j = 0, ll = names.length; j < ll; j += 1) {
                    const rawName = names[j];
                    let found = false;
                    for (let i = 0; i < newBackendNames.length; i += 1) {
                        if (rawName === newBackendNames[i]) {
                            newBackendNamesCount[i] += 1;
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        newBackendNames.push(rawName);
                        newBackendNameScopes.push(undefined);
                        newBackendNamesCount.push(1);
                    }
                }
            }
        }
        else {
            for (let i = 0, l = rawNames.length; i < l; i += 1) {
                const names = rawNames[i];
                if (!names)
                    continue;
                for (let j = 0, ll = names.length; j < ll; j += 1) {
                    const rawName = names[j];
                    this._$resolvePrefixes(rawName, (scopeId, className) => {
                        for (let i = 0; i < newBackendNames.length; i += 1) {
                            if (className === newBackendNames[i] && scopeId === newBackendNameScopes[i]) {
                                newBackendNamesCount[i] += 1;
                                return;
                            }
                        }
                        newBackendNames.push(className);
                        newBackendNameScopes.push(scopeId);
                        newBackendNamesCount.push(1);
                    });
                }
            }
        }
        let changed = false;
        for (let i = 0; i < newBackendNames.length; i += 1) {
            let found = false;
            for (let j = 0; j < oldBackendNames.length; j += 1) {
                if (newBackendNames[i] === oldBackendNames[j] &&
                    newBackendNameScopes[i] === oldBackendNameScopes[j]) {
                    found = true;
                    oldBackendNamesCount[j] = 0; // mark as exists
                    break;
                }
            }
            if (!found) {
                changed = true;
                this._$addClassToBackend(newBackendNames[i], newBackendNameScopes[i], backendElement);
            }
        }
        for (let j = 0; j < oldBackendNames.length; j += 1) {
            // 0 means old exists
            if (oldBackendNamesCount[j] !== 0) {
                changed = true;
                this._$removeClassFromBackend(oldBackendNames[j], oldBackendNameScopes[j], backendElement);
            }
        }
        this._$backendNames = newBackendNames;
        this._$backendNameScopes = newBackendNameScopes;
        this._$backendNamesCount = newBackendNamesCount;
        return changed;
    }
    /** @internal */
    _$addClassToBackend(name, scope, e) {
        var _a;
        if (BM.DOMLIKE || (BM.DYNAMIC && this._$element.getBackendMode() === 3 /* BackendMode.Domlike */)) {
            const prefix = scope === undefined ? '' : (_a = this._$prefixManager) === null || _a === void 0 ? void 0 : _a.queryName(scope);
            const val = prefix ? `${prefix}--${name}` : name;
            e.classList.add(val);
        }
        else if (BM.COMPOSED ||
            (BM.DYNAMIC && this._$element.getBackendMode() === 2 /* BackendMode.Composed */)) {
            ;
            e.addClass(name, scope);
        }
        else {
            ;
            e.addClass(name);
        }
    }
    /** @internal */
    _$removeClassFromBackend(name, scope, e) {
        var _a;
        if (BM.DOMLIKE || (BM.DYNAMIC && this._$element.getBackendMode() === 3 /* BackendMode.Domlike */)) {
            const prefix = scope && ((_a = this._$prefixManager) === null || _a === void 0 ? void 0 : _a.queryName(scope));
            const val = prefix ? `${prefix}--${name}` : name;
            e.classList.remove(val);
        }
        else if (BM.COMPOSED ||
            (BM.DYNAMIC && this._$element.getBackendMode() === 2 /* BackendMode.Composed */)) {
            ;
            e.removeClass(name, scope);
        }
        else {
            ;
            e.removeClass(name);
        }
    }
    /** @internal */
    _$addClass(name, scopeId, backendElement) {
        const oldClassNames = this._$backendNames;
        const oldScopeIds = this._$backendNameScopes;
        const classNamesCount = this._$backendNamesCount;
        let found = false;
        for (let j = 0; j < oldClassNames.length; j += 1) {
            if (name === oldClassNames[j] && scopeId === oldScopeIds[j]) {
                found = true;
                classNamesCount[j] += 1;
                break;
            }
        }
        if (!found) {
            oldClassNames.push(name);
            oldScopeIds.push(scopeId);
            classNamesCount.push(1);
            this._$addClassToBackend(name, scopeId, backendElement);
        }
    }
    /** @internal */
    _$removeClass(name, scopeId, backendElement) {
        const oldClassNames = this._$backendNames;
        const oldScopeIds = this._$backendNameScopes;
        const classNamesCount = this._$backendNamesCount;
        for (let j = 0; j < oldClassNames.length; j += 1) {
            if (name === oldClassNames[j] && scopeId === oldScopeIds[j]) {
                if (classNamesCount[j] <= 1) {
                    oldClassNames.splice(j, 1);
                    oldScopeIds.splice(j, 1);
                    classNamesCount.splice(j, 1);
                    this._$removeClassFromBackend(name, scopeId, backendElement);
                }
                else {
                    classNamesCount[j] -= 1;
                }
                break;
            }
        }
    }
    toggle(name, force, segmentIndex = 0 /* StyleSegmentIndex.MAIN */) {
        const backendElement = this._$element._$backendElement;
        const rawClassIndex = this._$rawNames[segmentIndex]
            ? this._$rawNames[segmentIndex].indexOf(name)
            : -1;
        const isAdd = force === undefined ? rawClassIndex === -1 : !!force;
        let changed = false;
        if (isAdd) {
            if (rawClassIndex === -1) {
                const rawNames = this._$rawNames;
                if (!rawNames[segmentIndex]) {
                    rawNames[segmentIndex] = [];
                }
                const names = rawNames[segmentIndex];
                names.push(name);
                if (backendElement) {
                    if (BM.SHADOW || (BM.DYNAMIC && this._$element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                        this._$addClass(name, undefined, backendElement);
                    }
                    else {
                        this._$resolvePrefixes(name, (scopeId, className) => {
                            this._$addClass(className, scopeId, backendElement);
                        });
                    }
                }
                changed = true;
            }
        }
        else if (rawClassIndex !== -1) {
            const names = this._$rawNames[segmentIndex];
            if (names)
                names.splice(rawClassIndex, 1);
            if (backendElement) {
                if (BM.SHADOW || (BM.DYNAMIC && this._$element.getBackendMode() === 1 /* BackendMode.Shadow */)) {
                    this._$removeClass(name, undefined, backendElement);
                }
                else {
                    this._$resolvePrefixes(name, (scopeId, className) => {
                        this._$removeClass(className, scopeId, backendElement);
                    });
                }
            }
            changed = true;
        }
        return changed;
    }
    contains(name, segmentIndex = 0 /* StyleSegmentIndex.MAIN */) {
        const names = this._$rawNames[segmentIndex] || [];
        for (let i = 0; i < names.length; i += 1) {
            const rn = names[i];
            if (rn[0] === '~') {
                const n = rn.slice(1);
                if (n === name)
                    return true;
            }
            else if (rn[0] === '^') {
                let n = rn.slice(1);
                while (n[0] === '^') {
                    n = n.slice(1);
                }
                if (n === name)
                    return true;
            }
            else {
                if (rn === name)
                    return true;
            }
        }
        return false;
    }
    setClassNames(names, segmentIndex = 0 /* StyleSegmentIndex.MAIN */) {
        let n;
        if (names === undefined || names === null) {
            n = [];
        }
        else if (Array.isArray(names)) {
            n = matchClassName(names.join(' '));
        }
        else {
            n = matchClassName(String(names));
        }
        const useFasterAdd = this._$rawNames.length === 0;
        this._$rawNames[segmentIndex] = n;
        if (useFasterAdd)
            return this._$fasterAddUpdateResolvedNames();
        return this._$updateResolvedNames();
    }
    /** Set class list */
    setClassNameList(names, segmentIndex = 0 /* StyleSegmentIndex.MAIN */) {
        const useFasterAdd = this._$rawNames.length === 0;
        this._$rawNames[segmentIndex] = names.filter((name) => name !== '');
        if (useFasterAdd)
            return this._$fasterAddUpdateResolvedNames();
        return this._$updateResolvedNames();
    }
    /** Returns space separated class string */
    getClassNames(segmentIndex = 0 /* StyleSegmentIndex.MAIN */) {
        const names = this._$rawNames[segmentIndex] || [];
        return names ? names.join(' ') : '';
    }
}
//# sourceMappingURL=class_list.js.map